import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Un 401 vol dir que la sessió ja no val: fora el token i cap al login.
    //
    // Excepte si el que ha fallat ÉS l'intent d'entrar o de registrar-se. Allà
    // el 401 és la resposta normal a una contrasenya equivocada, i recarregar
    // la pàgina esborrava el formulari i el missatge d'error tots dos: es veia
    // un parpelleig i prou, mai no sabies què havia passat.
    const url = error.config?.url || '';
    const esIntentDEntrar = url.includes('/api/auth/token') || url.includes('/api/auth/register');

    if (error.response?.status === 401 && !esIntentDEntrar) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  setToken: (token) => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  },

  login: async (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    
    const response = await api.post('/api/auth/token', formData);
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  }
};

export const projectService = {
  getProjects: async (includeArchived = false) => {
    const response = await api.get(`/api/projects/?include_archived=${includeArchived}`);
    return response.data;
  },

  getProject: async (id) => {
    const response = await api.get(`/api/projects/${id}`);
    return response.data;
  },

  createProject: async (projectData) => {
    const response = await api.post('/api/projects/', projectData);
    return response.data;
  },

  updateProject: async (id, projectData) => {
    const response = await api.put(`/api/projects/${id}`, projectData);
    return response.data;
  },

  deleteProject: async (id) => {
    const response = await api.delete(`/api/projects/${id}`);
    return response.data;
  },

  getWorkspaceColleagues: async () => {
    const response = await api.get('/api/projects/colleagues');
    return response.data;
  },

  addParticipant: async (projectId, userId) => {
    const response = await api.post(`/api/projects/${projectId}/participants/${userId}`);
    return response.data;
  },

  removeParticipant: async (projectId, userId) => {
    const response = await api.delete(`/api/projects/${projectId}/participants/${userId}`);
    return response.data;
  },

  createMessage: async (projectId, messageData) => {
    const response = await api.post(`/api/projects/${projectId}/messages`, messageData);
    return response.data;
  },

  getMessages: async (projectId) => {
    const response = await api.get(`/api/projects/${projectId}/messages`);
    return response.data;
  }
};

export const caseService = {
  getCases: async (workspaceId, includeArchived = false) => {
    const response = await api.get(`/api/cases/workspace/${workspaceId}?include_archived=${includeArchived}`);
    return response.data;
  },


  getCase: async (id) => {
    const response = await api.get(`/api/cases/${id}`);
    return response.data;
  },

  createCase: async (caseData) => {
    const response = await api.post('/api/cases/', caseData);
    return response.data;
  },

  updateCase: async (id, caseData) => {
    const response = await api.put(`/api/cases/${id}`, caseData);
    return response.data;
  },

  deleteCase: async (id) => {
    const response = await api.delete(`/api/cases/${id}`);
    return response.data;
  }
};

export const factorService = {
  createFactor: async (factorData) => {
    const response = await api.post('/api/factors/', factorData);
    return response.data;
  },

  getProjectFactors: async (projectId) => {
    const response = await api.get(`/api/factors/project/${projectId}`);
    return response.data;
  },

  getCaseFactors: async (caseId) => {
    const response = await api.get(`/api/factors/case/${caseId}`);
    return response.data;
  },

  updateFactor: async (id, factorData) => {
    const response = await api.put(`/api/factors/${id}`, factorData);
    return response.data;
  },

  updateDriverTest: async (id, testData) => {
    const response = await api.put(`/api/factors/${id}/driver-test`, testData);
    return response.data;
  },

  updateDriverExtremes: async (id, extremesData) => {
    const response = await api.put(`/api/factors/${id}/driver-extremes`, extremesData);
    return response.data;
  },

  getProjectDrivers: async (projectId) => {
    const response = await api.get(`/api/factors/project/${projectId}/drivers`);
    return response.data;
  },

  getCaseDrivers: async (caseId) => {
    const response = await api.get(`/api/factors/case/${caseId}/drivers`);
    return response.data;
  },

  assignSecondaryFactors: async (projectId, assignments) => {
    const response = await api.post(`/api/factors/project/${projectId}/secondary-factors`, assignments);
    return response.data;
  },

  assignCaseSecondaryFactors: async (caseId, assignments) => {
    const response = await api.post(`/api/factors/case/${caseId}/secondary-factors`, assignments);
    return response.data;
  },

  deleteFactor: async (id) => {
    const response = await api.delete(`/api/factors/${id}`);
    return response.data;
  }
};

export const scenarioService = {
  generateScenarios: async (projectId) => {
    const response = await api.post(`/api/scenarios/project/${projectId}/generate`);
    return response.data;
  },

  generateCaseScenarios: async (caseId) => {
    const response = await api.post(`/api/scenarios/case/${caseId}/generate`);
    return response.data;
  },

  getProjectScenarios: async (projectId) => {
    const response = await api.get(`/api/scenarios/project/${projectId}`);
    return response.data;
  },

  getCaseScenarios: async (caseId) => {
    const response = await api.get(`/api/scenarios/case/${caseId}`);
    return response.data;
  },

  updateScenario: async (id, scenarioData) => {
    const response = await api.put(`/api/scenarios/${id}`, scenarioData);
    return response.data;
  },

  toggleScenarioSelection: async (id) => {
    const response = await api.put(`/api/scenarios/${id}/select`);
    return response.data;
  }
};

export const narrativeService = {
  createNarrative: async (narrativeData) => {
    const response = await api.post('/api/narratives/', narrativeData);
    return response.data;
  },

  getScenarioNarratives: async (scenarioId) => {
    const response = await api.get(`/api/narratives/scenario/${scenarioId}`);
    return response.data;
  },

  updateNarrative: async (id, narrativeData) => {
    const response = await api.put(`/api/narratives/${id}`, narrativeData);
    return response.data;
  }
};

export const userService = {
  getUsers: async (params = {}) => {
    const response = await api.get('/api/users/', { params });
    return response.data;
  },

  // Altes que esperen activació. Només per a administradors.
  getPendingUsers: async () => {
    const response = await api.get('/api/users/pending');
    return response.data;
  },

  approveUser: async (id) => {
    const response = await api.post(`/api/users/${id}/approve`);
    return response.data;
  },

  // Descarta l'alta esborrant el compte. El backend només ho deixa fer amb
  // comptes inactius: un usuari que ja ha estat actiu pot tenir feina penjant-ne.
  rejectUser: async (id) => {
    await api.delete(`/api/users/${id}/reject`);
  },

  getUser: async (id) => {
    const response = await api.get(`/api/users/${id}`);
    return response.data;
  },

  createUser: async (userData) => {
    const response = await api.post('/api/users/', userData);
    return response.data;
  },

  updateUser: async (id, userData) => {
    const response = await api.put(`/api/users/${id}`, userData);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/api/users/${id}`);
    return response.data;
  },

  changePassword: async (id, passwordData) => {
    const response = await api.post(`/api/users/${id}/change-password`, passwordData);
    return response.data;
  },

  getOrganizations: async () => {
    const response = await api.get('/api/users/organizations/list');
    return response.data;
  }
};

export const caseNotesService = {
  createNote: async (noteData) => {
    const response = await api.post('/api/case-notes/', noteData);
    return response.data;
  },

  getCaseNotes: async (caseId) => {
    const response = await api.get(`/api/case-notes/case/${caseId}`);
    return response.data;
  },

  getNote: async (noteId) => {
    const response = await api.get(`/api/case-notes/${noteId}`);
    return response.data;
  },

  updateNote: async (noteId, noteData) => {
    const response = await api.put(`/api/case-notes/${noteId}`, noteData);
    return response.data;
  },

  deleteNote: async (noteId) => {
    const response = await api.delete(`/api/case-notes/${noteId}`);
    return response.data;
  }
};

// Strategy Mapping Service
export const strategyMappingService = {
  // Get complete strategy mapping
  getStrategyMapping: (caseId) => api.get(`/api/strategy-mapping/cases/${caseId}/strategy-mapping`).then(res => res.data),
  
  // Actor operations
  getActors: (caseId) => api.get(`/api/strategy-mapping/cases/${caseId}/strategy-actors`).then(res => res.data),
  createActor: (caseId, data) => api.post(`/api/strategy-mapping/cases/${caseId}/strategy-actors`, data).then(res => res.data),
  updateActor: (actorId, data) => api.put(`/api/strategy-mapping/strategy-actors/${actorId}`, data).then(res => res.data),
  deleteActor: (actorId) => api.delete(`/api/strategy-mapping/strategy-actors/${actorId}`).then(res => res.data),
  
  // Element operations
  getElements: (caseId) => api.get(`/api/strategy-mapping/cases/${caseId}/strategy-elements`).then(res => res.data),
  createElement: (caseId, data) => api.post(`/api/strategy-mapping/cases/${caseId}/strategy-elements`, data).then(res => res.data),
  updateElement: (elementId, data) => api.put(`/api/strategy-mapping/strategy-elements/${elementId}`, data).then(res => res.data),
  deleteElement: (elementId) => api.delete(`/api/strategy-mapping/strategy-elements/${elementId}`).then(res => res.data),
  
  // Connection operations
  getConnections: (caseId) => api.get(`/api/strategy-mapping/cases/${caseId}/strategy-connections`).then(res => res.data),
  createConnection: (caseId, data) => api.post(`/api/strategy-mapping/cases/${caseId}/strategy-connections`, data).then(res => res.data),
  updateConnection: (connectionId, data) => api.put(`/api/strategy-mapping/strategy-connections/${connectionId}`, data).then(res => res.data),
  deleteConnection: (connectionId) => api.delete(`/api/strategy-mapping/strategy-connections/${connectionId}`).then(res => res.data),
  
  // Convert elements to causal factors
  convertToFactors: (caseId, data) => api.post(`/api/strategy-mapping/cases/${caseId}/strategy-mapping/convert-to-factors`, data).then(res => res.data)
};

// Admin Service
export const adminService = {
  // Email configuration
  getEmailConfig: async () => {
    const response = await api.get('/api/admin/email/config');
    return response.data;
  },

  updateEmailConfig: async (config) => {
    const response = await api.post('/api/admin/email/config', config);
    return response.data;
  },

  getEmailStatus: async () => {
    const response = await api.get('/api/admin/email/status');
    return response.data;
  },

  testEmail: async (testData) => {
    const response = await api.post('/api/admin/email/test', testData);
    return response.data;
  },

  getEmailTemplates: async () => {
    const response = await api.get('/api/admin/email/templates');
    return response.data;
  },

  getTemplateContent: async (templateName, language) => {
    const response = await api.get(`/api/admin/email/templates/${templateName}/${language}`);
    return response.data;
  },

  updateTemplateContent: async (templateName, language, content) => {
    const response = await api.put(`/api/admin/email/templates/${templateName}/${language}`, { content });
    return response.data;
  },

  // System information
  getSystemInfo: async () => {
    const response = await api.get('/api/admin/system/info');
    return response.data;
  },

  // Send custom email to user
  sendCustomEmail: async (emailData) => {
    const response = await api.post('/api/admin/email/send-custom', emailData);
    return response.data;
  }
};

// Backcasting
// Crear i llistar pengen de l'exercici; modificar i esborrar, de l'objecte.
// És el mateix repartiment que fa strategyMappingService.
export const backcastingService = {
  getExercises: (caseId) => api.get(`/api/backcasting/cases/${caseId}/exercises`).then(r => r.data),
  createExercise: (caseId, data) => api.post(`/api/backcasting/cases/${caseId}/exercises`, data).then(r => r.data),
  getExercise: (exerciseId) => api.get(`/api/backcasting/exercises/${exerciseId}`).then(r => r.data),
  updateExercise: (exerciseId, data) => api.put(`/api/backcasting/exercises/${exerciseId}`, data).then(r => r.data),
  deleteExercise: (exerciseId) => api.delete(`/api/backcasting/exercises/${exerciseId}`).then(r => r.data),

  createDifference: (exerciseId, data) => api.post(`/api/backcasting/exercises/${exerciseId}/differences`, data).then(r => r.data),
  updateDifference: (id, data) => api.put(`/api/backcasting/differences/${id}`, data).then(r => r.data),
  deleteDifference: (id) => api.delete(`/api/backcasting/differences/${id}`).then(r => r.data),

  createMilestone: (exerciseId, data) => api.post(`/api/backcasting/exercises/${exerciseId}/milestones`, data).then(r => r.data),
  updateMilestone: (id, data) => api.put(`/api/backcasting/milestones/${id}`, data).then(r => r.data),
  deleteMilestone: (id) => api.delete(`/api/backcasting/milestones/${id}`).then(r => r.data),
  reorderMilestones: (exerciseId, order) => api.put(`/api/backcasting/exercises/${exerciseId}/milestones/reorder`, order).then(r => r.data),

  createAction: (exerciseId, data) => api.post(`/api/backcasting/exercises/${exerciseId}/actions`, data).then(r => r.data),
  updateAction: (id, data) => api.put(`/api/backcasting/actions/${id}`, data).then(r => r.data),
  deleteAction: (id) => api.delete(`/api/backcasting/actions/${id}`).then(r => r.data)
};

export default api;